"use client";

import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { Person } from "@/lib/types";
import { BlurImage } from "@/components/ui/BlurImage";

function groupSlug(name: string) {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "group";
}

/**
 * big.dk people: left-rail hash links, name+role rows, hover scale 1.55,
 * radio accordion (+ rotates) revealing the portrait. No bio field yet.
 */
export function PeopleView({ title, people }: { title: string; people: Person[] }) {
  const groups = useMemo(() => [...new Set(people.map((p) => p.group))], [people]);
  const [active, setActive] = useState(groups[0] ? groupSlug(groups[0]) : "");

  useEffect(() => {
    const sections = groups
      .map((g) => document.getElementById(groupSlug(g)))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const id = visible[0]?.target.id;
        if (id) setActive(id);
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.15, 0.4] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [groups]);

  return (
    <div className="page-container pb-24">
      <div className="pointer-events-none top-0 bottom-0 left-0 z-20 hidden flex-col justify-center gap-[5px] text-xs uppercase lg:fixed lg:flex lg:h-screen">
        {groups.map((g) => {
          const id = groupSlug(g);
          const isActive = active === id;
          return (
            <a
              key={id}
              href={`#${id}`}
              className={clsx(
                "pointer-events-auto relative flex items-center gap-[5px] py-[4px] pr-0 pl-[30px] xl:pr-[30px] xl:pl-[40px]",
                isActive
                  ? "text-black before:absolute before:top-0 before:bottom-0 before:left-[20px] before:my-auto before:inline-block before:h-[5px] before:w-[5px] before:-translate-y-[0.5px] before:bg-black xl:before:left-[30px]"
                  : "text-[#898989]",
              )}
            >
              {g}
            </a>
          );
        })}
      </div>

      <div className="mx-auto px-7 md:px-17.5 lg:max-w-[64%] lg:px-25 xl:max-w-[60%]">
        <h1 className="studio-title mb-5 -translate-x-1.5 lg:mb-15">
          {title}
        </h1>

        {groups.map((g, gi) => {
          const id = groupSlug(g);
          const radioName = `people-${id}-accordion`;
          return (
            <section
              key={id}
              id={id}
              className={clsx("scroll-mt-[40vh]", gi === 0 ? "mt-0" : "mt-0 md:mt-24")}
            >
              <h2 className="mb-4 text-xs text-[#9e9e9e] uppercase">{g}</h2>
              {people
                .filter((p) => p.group === g)
                .map((p) => (
                  <PersonRow key={p.id} person={p} radioName={radioName} />
                ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function PersonRow({ person, radioName }: { person: Person; radioName: string }) {
  const inputId = `person-${person.id}`;

  return (
    <div className="group" data-person-row="">
      <label htmlFor={inputId} className="block" aria-label={person.fullName}>
        <input
          id={inputId}
          type="radio"
          name={radioName}
          className="peer sr-only"
          onMouseDown={(e) => {
            e.currentTarget.dataset.wasChecked = e.currentTarget.checked ? "1" : "0";
          }}
          onClick={(e) => {
            if (e.currentTarget.dataset.wasChecked === "1") {
              e.currentTarget.checked = false;
            }
          }}
        />
        <div className="group grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 py-1.5">
          {person.photoUrl ? (
            <span className="relative h-8 w-8 shrink-0 overflow-hidden bg-neutral-200">
              <BlurImage
                fill
                src={person.photoUrl}
                alt=""
                width={64}
                height={64}
                blurDataUrl={person.photoBlurDataUrl}
                className="object-cover object-center"
                sizes="32px"
              />
            </span>
          ) : (
            <span className="h-8 w-8 shrink-0" aria-hidden />
          )}
          <div className="origin-left duration-500 ease-out group-hover:scale-[1.55] group-hover:duration-400">
            <span className="mr-1 ml-0 inline-block origin-center -translate-y-0.5 text-xs font-medium text-[#797979] transition-transform group-peer-checked:rotate-45">
              +
            </span>
            {person.fullName}
          </div>
          <div className="ml-auto truncate text-right text-xs text-[#9e9e9e] uppercase lg:max-w-50 xl:max-w-none">
            {person.title}
          </div>
        </div>
        {person.photoUrl ? (
          <div
            className={clsx(
              "z-20 max-h-0 w-full overflow-hidden opacity-0 transition-all duration-280 ease-in-out",
              "max-lg:peer-checked:max-h-75 max-lg:peer-checked:opacity-100",
              "lg:pointer-events-none lg:fixed lg:top-83 lg:right-5 lg:mt-0 lg:max-h-none lg:max-w-65 lg:overflow-auto lg:delay-0",
              "lg:group-hover:pointer-events-auto lg:group-hover:opacity-100 lg:group-hover:delay-220 lg:peer-checked:opacity-100",
              "xl:max-w-85 2xl:max-w-97.5",
            )}
          >
            <div className="relative mt-2 mb-4 aspect-3/4 w-full max-lg:max-w-60 bg-neutral-200 lg:mt-0 lg:mb-0 lg:max-w-none">
              <BlurImage
                fill
                src={person.photoUrl}
                alt={person.fullName}
                width={600}
                height={800}
                blurDataUrl={person.photoBlurDataUrl}
                className="object-cover object-center"
                sizes="(max-width: 1024px) 240px, 340px"
              />
            </div>
          </div>
        ) : null}
      </label>
    </div>
  );
}
